// Scanner de QR Code (caméra + jsQR), partagé par l'écran Pointage (scan du responsable, secours)
// et la page de borne (temps/BornePage). Ne lit que les codes « SOGAS:… » ; rend le contenu à onScan.
import { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import { Camera, X } from 'lucide-react';

const VERT  = '#1B5E20';
const ROUGE = '#C62828';

const ScannerQR = ({ onScan, onFermer, titre = 'Scanner le QR Code', consigne = 'Pointez la caméra vers le QR Code de l\'employé' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);
  const [erreurCam, setErreurCam] = useState('');
  const [scanEnCours, setScanEnCours] = useState(false);

  const demarrerCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (e) {
      setErreurCam('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
    }
  }, []);

  const arreterCamera = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, []);

  useEffect(() => {
    demarrerCamera();
    return () => arreterCamera();
  }, [demarrerCamera, arreterCamera]);

  useEffect(() => {
    const analyser = () => {
      if (!videoRef.current || !canvasRef.current || scanEnCours) {
        animRef.current = requestAnimationFrame(analyser);
        return;
      }
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        animRef.current = requestAnimationFrame(analyser);
        return;
      }
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        animRef.current = requestAnimationFrame(analyser);
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      let imgData;
      try {
        imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      } catch (e) {
        animRef.current = requestAnimationFrame(analyser);
        return;
      }
      let code = null;
      try {
        code = jsQR(imgData.data, imgData.width, imgData.height, { inversionAttempts: 'dontInvert' });
      } catch (e) {
        animRef.current = requestAnimationFrame(analyser);
        return;
      }
      if (code && code.data && code.data.startsWith('SOGAS:')) {
        setScanEnCours(true);
        arreterCamera();
        onScan(code.data);
        return;
      }
      animRef.current = requestAnimationFrame(analyser);
    };
    animRef.current = requestAnimationFrame(analyser);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [scanEnCours, onScan, arreterCamera]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: VERT, fontSize: 17, display: 'flex', alignItems: 'center', gap: 6 }}><Camera size={16} />{titre}</h3>
          <button onClick={() => { arreterCamera(); onFermer(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center' }}><X size={20} /></button>
        </div>

        {erreurCam ? (
          <div style={{ padding: 16, background: '#FFEBEE', color: ROUGE, borderRadius: 8, marginBottom: 12 }}>
            {erreurCam}
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Vous pouvez aussi entrer le code manuellement.</div>
          </div>
        ) : (
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 12 }}>
            <video ref={videoRef} style={{ width: '100%', display: 'block', maxHeight: 320 }} muted playsInline />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: 200, height: 200, border: '3px solid rgba(255,255,255,0.8)', borderRadius: 16, boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)' }}>
                <div style={{ position: 'absolute', top: -2, left: -2, width: 24, height: 24, borderTop: `4px solid ${VERT}`, borderLeft: `4px solid ${VERT}`, borderRadius: '4px 0 0 0' }} />
                <div style={{ position: 'absolute', top: -2, right: -2, width: 24, height: 24, borderTop: `4px solid ${VERT}`, borderRight: `4px solid ${VERT}`, borderRadius: '0 4px 0 0' }} />
                <div style={{ position: 'absolute', bottom: -2, left: -2, width: 24, height: 24, borderBottom: `4px solid ${VERT}`, borderLeft: `4px solid ${VERT}`, borderRadius: '0 0 0 4px' }} />
                <div style={{ position: 'absolute', bottom: -2, right: -2, width: 24, height: 24, borderBottom: `4px solid ${VERT}`, borderRight: `4px solid ${VERT}`, borderRadius: '0 0 4px 0' }} />
              </div>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <p style={{ fontSize: 12, color: '#888', margin: '8px 0 0' }}>{consigne}</p>
      </div>
    </div>
  );
};

export default ScannerQR;
